"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var recompose_1 = require("recompose");
var hocs_1 = require("../hocs");
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var styled_components_1 = require("styled-components");
var iconCross_svg_1 = tslib_1.__importDefault(require("../../icons/iconCross.svg"));
var Box_1 = require("../Box");
var Button_1 = require("../Button");
var Drop_1 = require("../Drop");
var FormLabel_1 = require("../FormLabel");
var InfiniteScroll_1 = require("../InfiniteScroll");
var Keyboard_1 = require("../Keyboard");
var withFormFields_hoc_1 = require("../withFormFields/withFormFields.hoc");
var StyledTextInput_1 = require("./StyledTextInput");
function getLabel(suggestion) {
    if (suggestion && typeof suggestion === 'object') {
        return suggestion.label || suggestion.value.toString();
    }
    return suggestion;
}
/**
 * @visibleName FormTextInput
 */
exports.getSuggestionSearchValue = function (suggestion) {
    if (suggestion && typeof suggestion === 'object') {
        if (suggestion.searchText || suggestion.searchText === '') {
            return suggestion.searchText;
        }
        if (suggestion.label && !React.isValidElement(suggestion.label)) {
            return suggestion.label.toString();
        }
        return suggestion.value.toString();
    }
    return suggestion;
};
var TextInput = /** @class */ (function (_super) {
    tslib_1.__extends(TextInput, _super);
    function TextInput() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.inputRef = _this.props.forwardRef || React.createRef();
        _this.suggestionRefs = [];
        _this.state = {
            activeSuggestionIndex: -1,
            showDrop: false
        };
        _this.onClickSuggestion = function (suggestion) {
            _this.closeSuggestions();
            _this.handleChange(exports.getSuggestionSearchValue(suggestion));
            if (_this.inputRef.current) {
                _this.inputRef.current.focus();
            }
        };
        _this.onKeyDown = function (event) {
            var _a = _this.props, onKeyDown = _a.onKeyDown, suggestions = _a.suggestions;
            if (_this.state.showDrop && suggestions && suggestions.length) {
                var key = event.keyCode ? event.keyCode : event.which;
                var currentSuggestionIndex = _this.state.activeSuggestionIndex;
                var handled = false;
                var newIndex = void 0;
                switch (key) {
                    case 13: // enter
                        handled = true;
                        _this.onClickSuggestion(suggestions[currentSuggestionIndex]);
                        break;
                    case 38: // up
                        if (currentSuggestionIndex > 0) {
                            handled = true;
                            newIndex = currentSuggestionIndex - 1;
                            _this.setState({
                                activeSuggestionIndex: newIndex
                            });
                        }
                        break;
                    case 40: // down
                        if (currentSuggestionIndex < suggestions.length - 1) {
                            handled = true;
                            newIndex = currentSuggestionIndex + 1;
                            _this.setState({
                                activeSuggestionIndex: newIndex
                            });
                        }
                        break;
                }
                if (handled) {
                    event.preventDefault();
                }
                if (newIndex) {
                    var ref = _this.suggestionRefs[newIndex].current;
                    if (ref) {
                        ref.scrollIntoView({ block: 'end', behavior: 'smooth' });
                    }
                }
            }
            if (onKeyDown) {
                onKeyDown(event);
            }
        };
        _this.onBlur = function (event) {
            var onBlur = _this.props.onBlur;
            _this.closeSuggestions();
            clearTimeout(_this.resetTimer);
            if (onBlur) {
                onBlur(event);
            }
        };
        _this.onChange = function (value) {
            _this.resetSuggestions();
            _this.handleChange(value);
        };
        _this.resetSuggestions = function () {
            // 10ms was chosen empirically based on ie11 using TextInput
            // with and without a FormField.
            clearTimeout(_this.resetTimer);
            _this.resetTimer = setTimeout(function () {
                var _a = _this.props, selectedValue = _a.selectedValue, suggestions = _a.suggestions;
                var activeSuggestionIndex = selectedValue && suggestions
                    ? suggestions.findIndex(function (s) {
                        return isEqual_1.default(typeof s === 'string' ? s : s.value, selectedValue);
                    })
                    : -1;
                _this.setState({
                    activeSuggestionIndex: activeSuggestionIndex,
                    showDrop: true
                });
            }, 10);
        };
        _this.closeSuggestions = function () {
            _this.setState({ showDrop: false });
        };
        _this.renderSuggestions = function () {
            var _a = _this.props, suggestions = _a.suggestions, id = _a.id;
            var activeSuggestionIndex = _this.state.activeSuggestionIndex;
            _this.suggestionRefs = [];
            var renderSuggestion = function (suggestion) {
                var plain = typeof suggestion === 'object' && React.isValidElement(suggestion.label);
                return plain ? (getLabel(suggestion)) : (React.createElement(StyledTextInput_1.StyledBox, { align: "start", pad: "xsmall" }, getLabel(suggestion)));
            };
            return (React.createElement(StyledTextInput_1.StyledSuggestions, null,
                React.createElement(InfiniteScroll_1.InfiniteScroll, { items: suggestions, step: suggestions && suggestions.length }, function (suggestion, index) {
                    _this.suggestionRefs.push(React.createRef());
                    return (React.createElement("li", tslib_1.__assign({ key: getLabel(suggestion) + "-" + index, id: id && id + "-" + index, ref: _this.suggestionRefs[index] }, suggestion.listProps), suggestion.displayOnly ? (renderSuggestion(suggestion)) : (React.createElement(Button_1.Button, { active: activeSuggestionIndex === index, tabIndex: -1, fill: true, hoverIndicator: "background", onClick: function () { return _this.onClickSuggestion(suggestion); }, disabled: suggestion.displayOnly }, renderSuggestion(suggestion)))));
                })));
        };
        _this.clearValue = function () {
            _this.onChange('');
        };
        _this.handleChange = function (value) {
            var _a = _this.props, onChange = _a.onChange, onTextChange = _a.onTextChange, onSelect = _a.onSelect, name = _a.name, suggestions = _a.suggestions;
            var changeWasSelection = false;
            if (suggestions && suggestions.length) {
                var matchedSuggestion = value &&
                    suggestions.find(function (s) { return exports.getSuggestionSearchValue(s).toLowerCase() === value.toLowerCase(); });
                var matchedValue = matchedSuggestion && (typeof matchedSuggestion === 'object' ? matchedSuggestion.value : matchedSuggestion);
                if (matchedValue) {
                    changeWasSelection = true;
                }
                var matchedSearchText = matchedSuggestion && exports.getSuggestionSearchValue(matchedSuggestion);
                // Correct casing
                if (matchedSearchText && matchedSearchText !== value) {
                    value = matchedSearchText;
                }
                if (onSelect) {
                    onSelect({ target: { name: name, value: matchedValue } });
                }
            }
            if (onChange) {
                onChange({ persist: function () { return null; }, preventDefault: function () { return null; }, target: { name: name, value: value } });
            }
            if (onTextChange) {
                onTextChange({ changeWasSelection: changeWasSelection, target: { name: name, value: value } });
            }
        };
        return _this;
    }
    TextInput.prototype.componentWillUnmount = function () {
        clearTimeout(this.resetTimer);
    };
    TextInput.prototype.render = function () {
        var _this = this;
        var _a = this.props, defaultValue = _a.defaultValue, dropTarget = _a.dropTarget, forwardRef = _a.forwardRef, id = _a.id, autoComplete = _a.autoComplete, placeholder = _a.placeholder, label = _a.label, theme = _a.theme, value = _a.value, selectedValue = _a.selectedValue, onKeyDown = _a.onKeyDown, error = _a.error, errorMessage = _a.errorMessage, focus = _a.focus, valid = _a.valid, icon = _a.icon, inputIcon = _a.inputIcon, onIconClick = _a.onIconClick, enableClear = _a.enableClear, suggestions = _a.suggestions, onChange = _a.onChange, onTextChange = _a.onTextChange, onSuggestionsOpen = _a.onSuggestionsOpen, onSuggestionsClose = _a.onSuggestionsClose, 
        // Needed so that styled components does not invoke onSelect when text input is clicked
        onSelect = _a.onSelect, children = _a.children, topOffsetValue = _a.topOffsetValue, inline = _a.inline, verticalAlign = _a.verticalAlign, rest = tslib_1.__rest(_a, ["defaultValue", "dropTarget", "forwardRef", "id", "autoComplete", "placeholder", "label", "theme", "value", "selectedValue", "onKeyDown", "error", "errorMessage", "focus", "valid", "icon", "inputIcon", "onIconClick", "enableClear", "suggestions", "onChange", "onTextChange", "onSuggestionsOpen", "onSuggestionsClose", "onSelect", "children", "topOffsetValue", "inline", "verticalAlign"]);
        var showDrop = this.state.showDrop;
        var matchedSuggestion = selectedValue &&
            suggestions &&
            suggestions.find(function (s) { return isEqual_1.default(typeof s === 'string' ? s : s.value, selectedValue); });
        var effectiveValue = matchedSuggestion ? exports.getSuggestionSearchValue(matchedSuggestion) : value;
        return (React.createElement(StyledTextInput_1.StyledTextInputContainer, { inline: inline, verticalAlign: verticalAlign },
            label && (React.createElement(FormLabel_1.FormLabel, { htmlFor: id },
                React.createElement(Box_1.Box, { direction: "row", width: "100%" },
                    React.createElement(Box_1.Box, { align: "start", width: "100%", "data-testid": "label" }, label),
                    React.createElement(Box_1.Box, { align: "end" }, icon)))),
            placeholder && typeof placeholder !== 'string' && !effectiveValue ? (React.createElement(StyledTextInput_1.StyledPlaceholder, { theme: theme }, placeholder)) : null,
            React.createElement(StyledTextInput_1.StyledInputWrapper, { focus: focus, inline: inline },
                React.createElement(Keyboard_1.Keyboard, { onKeyDown: this.onKeyDown },
                    React.createElement(StyledTextInput_1.StyledTextInput, tslib_1.__assign({ id: id, ref: this.inputRef, autoComplete: autoComplete || '-', placeholder: typeof placeholder === 'string' ? placeholder : undefined, theme: theme, defaultValue: defaultValue, value: effectiveValue, error: error, valid: valid, inline: inline, onBlur: this.onBlur, onChange: function (_a) {
                            var target = _a.target;
                            return _this.onChange(target.value);
                        }, "aria-invalid": !!error }, rest))),
                inputIcon ? (React.createElement(StyledTextInput_1.StyledInputIcon, null, inputIcon)) : enableClear && effectiveValue ? (React.createElement(StyledTextInput_1.StyledClearIcon, { onClick: this.clearValue, "data-testid": "clear-icon" },
                    React.createElement("img", { src: iconCross_svg_1.default, alt: "clear" }))) : null,
                children),
            showDrop && suggestions && suggestions.length ? (React.createElement(Drop_1.Drop, { id: id ? "text-input-drop__" + id : undefined, responsive: false, restrictFocus: false, tabIndex: -1, target: dropTarget || this.inputRef.current, onClickOutside: this.closeSuggestions, onEsc: this.closeSuggestions, topOffsetValue: topOffsetValue }, this.renderSuggestions())) : null,
            error && (React.createElement(StyledTextInput_1.StyledError, { theme: theme, id: id && "error-" + id, "data-testid": "error-msg" }, errorMessage))));
    };
    TextInput.defaultProps = {
        value: '',
        messages: {
            enterSelect: '(Press Enter to Select)',
            suggestionsCount: 'suggestions available',
            suggestionsExist: 'This input has suggestions use arrow keys to navigate',
            suggestionIsOpen: 'Suggestions drop is open, continue to use arrow keys to navigate'
        }
    };
    return TextInput;
}(React.PureComponent));
var TextInputWrapper = recompose_1.compose(hocs_1.withFocus, styled_components_1.withTheme, hocs_1.withForwardRef, hocs_1.withAnnounce)(TextInput);
exports.TextInput = TextInputWrapper;
/** @component */
exports.FormTextInput = withFormFields_hoc_1.withFormFields(hocs_1.withFocus(TextInput));
exports.FormTextSelectionInput = withFormFields_hoc_1.withFormFields(TextInput, 'selectedValue', 'onSelect');
//# sourceMappingURL=TextInput.js.map